import React from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { COMPETENCIES, PHASES, TIER_MODELS, SCALE_MAX, scoreOf, readinessPct } from "@/data/coachingData";
import { useI18n } from "@/i18n";
import { Scale, ArrowUp, ArrowDown, Minus } from "lucide-react";

const COLORS = { a: "#6366F1", b: "#F59E0B" };

const highPriority = (sc) =>
  sc.recommendations?.programs?.filter((p) => p.priority === "High").length || 0;

const readiness = (sc) => {
  if (sc.recommendations?.org_readiness_score != null) return sc.recommendations.org_readiness_score;
  const keys = Object.keys(sc.scores || {});
  if (!keys.length) return 0;
  const avg =
    keys.reduce((a, k) => {
      const vals = COMPETENCIES.map((c) => scoreOf(sc.scores, k, c.key));
      return a + vals.reduce((x, y) => x + y, 0) / vals.length;
    }, 0) / keys.length;
  return readinessPct(avg);
};

const phaseAvgOf = (sc, phase) => {
  const keys = Object.keys(sc.scores || {});
  if (!keys.length) return 0;
  const avg =
    keys.reduce((a, k) => {
      const vals = phase.comps.map((c) => scoreOf(sc.scores, k, c.key));
      return a + vals.reduce((x, y) => x + y, 0) / vals.length;
    }, 0) / keys.length;
  return +avg.toFixed(1);
};

export function CompareView({ pair }) {
  const { t, locale } = useI18n();

  if (!pair || pair.length !== 2) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="compare-empty">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-500/10 mb-4">
          <Scale size={28} className="text-indigo-400" />
        </span>
        <h3 className="font-heading text-xl font-bold text-slate-100 mb-2">{t("cmp.emptyTitle")}</h3>
        <p className="text-sm text-slate-500 max-w-md">{t("cmp.emptyDesc")}</p>
      </div>
    );
  }

  const [a, b] = pair;

  const chartData = PHASES.map((p) => ({
    name: t(`phase.${p.key}.title`).split(" · ").pop(),
    A: phaseAvgOf(a, p),
    B: phaseAvgOf(b, p),
  }));

  const sameModel = a.model_type === b.model_type;
  const tiers = sameModel ? TIER_MODELS[a.model_type] || [] : [];

  return (
    <div className="space-y-6" data-testid="compare-view">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[a, b].map((sc, i) => (
          <motion.div
            key={sc.id}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
          >
            <Card
              className="bg-[#111827] border-slate-800 p-5 relative overflow-hidden"
              data-testid={`compare-header-${i === 0 ? "a" : "b"}`}
            >
              <span
                className="absolute top-0 left-0 w-full h-1"
                style={{ background: i === 0 ? COLORS.a : COLORS.b }}
              />
              <p
                className="text-xs uppercase tracking-widest font-semibold"
                style={{ color: i === 0 ? COLORS.a : COLORS.b }}
              >
                {t("cmp.company", { letter: i === 0 ? "A" : "B" })}
              </p>
              <h3 className="font-heading text-2xl font-extrabold text-slate-50 mt-1 truncate">
                {sc.company_name || sc.company}
              </h3>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <Badge className="bg-slate-800 text-slate-300 border-slate-700">
                  {t(`model.${sc.model_type}`)}
                </Badge>
                <Badge className="bg-slate-800 text-slate-300 border-slate-700">
                  {new Date(sc.created_at).toLocaleDateString(locale, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </Badge>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CompareKpi label={t("cmp.kpiReadiness")} aVal={readiness(a)} bVal={readiness(b)} suffix="%" />
        <CompareKpi label={t("cmp.kpiHigh")} aVal={highPriority(a)} bVal={highPriority(b)} />
      </div>

      <Card className="bg-[#111827] border-slate-800 p-5">
        <h3 className="font-heading text-base md:text-lg font-semibold text-slate-100 mb-1">
          {t("cmp.avgTitle")}
        </h3>
        <p className="text-xs text-slate-500 mb-4">{t("cmp.avgSub")}</p>
        <div className="h-[320px] w-full" data-testid="compare-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fill: "#9CA3AF", fontSize: 10 }}
                axisLine={{ stroke: "#1F2937" }}
                interval={0}
                angle={-15}
                textAnchor="end"
                height={60}
              />
              <YAxis domain={[0, SCALE_MAX]} tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={{ stroke: "#1F2937" }} />
              <Tooltip
                cursor={{ fill: "#ffffff08" }}
                contentStyle={{ background: "#0B0F17", border: "1px solid #1F2937", borderRadius: 12, color: "#F9FAFB", fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 12, color: "#9CA3AF" }} />
              <Bar dataKey="A" name={a.company_name || a.company} fill={COLORS.a} radius={[4, 4, 0, 0]} maxBarSize={32} />
              <Bar dataKey="B" name={b.company_name || b.company} fill={COLORS.b} radius={[4, 4, 0, 0]} maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {sameModel && (
        <Card className="bg-[#111827] border-slate-800 p-5">
          <h3 className="font-heading text-base md:text-lg font-semibold text-slate-100 mb-1">
            {t("cmp.byLevelTitle")}
          </h3>
          <p className="text-xs text-slate-500 mb-4">
            {t("cmp.byLevelSub", { model: t(`model.${a.model_type}`) })}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="compare-tier-table">
              <thead>
                <tr className="border-b border-slate-700 text-left">
                  <th className="py-2 pr-3 text-slate-400 font-semibold">{t("cmp.level")}</th>
                  <th className="py-2 px-3 text-slate-400 font-semibold text-center">
                    {a.company_name || a.company}
                  </th>
                  <th className="py-2 px-3 text-slate-400 font-semibold text-center">
                    {b.company_name || b.company}
                  </th>
                  <th className="py-2 pl-3 text-slate-400 font-semibold text-center">{t("cmp.delta")}</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier) => {
                  const avgOf = (sc) => {
                    const vals = COMPETENCIES.map((c) => scoreOf(sc.scores, tier.key, c.key));
                    return vals.reduce((x, y) => x + y, 0) / vals.length;
                  };
                  const av = avgOf(a);
                  const bv = avgOf(b);
                  const delta = +(bv - av).toFixed(1);
                  return (
                    <tr key={tier.key} className="border-b border-slate-800">
                      <td className="py-2.5 pr-3 font-medium text-slate-200">{t(`tier.${tier.key}`)}</td>
                      <td className="py-2.5 px-3 text-center font-mono text-indigo-300">{av.toFixed(1)}</td>
                      <td className="py-2.5 px-3 text-center font-mono text-amber-300">{bv.toFixed(1)}</td>
                      <td className="py-2.5 pl-3 text-center">
                        <DeltaBadge value={delta} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!sameModel && (
        <Card className="bg-[#111827] border-slate-800 p-5">
          <p className="text-sm text-slate-400">
            {t("cmp.diffModels", { a: t(`model.${a.model_type}`), b: t(`model.${b.model_type}`) })}
          </p>
        </Card>
      )}
    </div>
  );
}

function CompareKpi({ label, aVal, bVal, suffix = "" }) {
  const fmt = (v) => `${v}${suffix}`;
  const delta = bVal - aVal;
  return (
    <Card className="bg-[#111827] border-slate-800 p-5">
      <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">{label}</p>
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs text-slate-500">A</p>
          <p className="font-heading text-2xl font-extrabold text-indigo-300 tabular-nums">{fmt(aVal)}</p>
        </div>
        <DeltaBadge value={+delta.toFixed(1)} />
        <div className="text-right">
          <p className="text-xs text-slate-500">B</p>
          <p className="font-heading text-2xl font-extrabold text-amber-300 tabular-nums">{fmt(bVal)}</p>
        </div>
      </div>
    </Card>
  );
}

function DeltaBadge({ value, invert = false, suffix = "" }) {
  const positive = value > 0;
  const neutral = value === 0;
  const good = neutral ? null : invert ? !positive : positive;
  const Icon = neutral ? Minus : positive ? ArrowUp : ArrowDown;
  const color = neutral
    ? "bg-slate-800 text-slate-400 border-slate-700"
    : good
    ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
    : "bg-rose-500/15 text-rose-400 border-rose-500/30";
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${color}`}>
      <Icon size={12} />
      {neutral ? "0" : `${positive ? "+" : ""}${value}${suffix}`}
    </span>
  );
}
